import './feature-guide-cards.scss';
import { useContext, useMemo } from 'react';
import ServerContext from '../../../../contexts/serverContext';
import { EligibleModelData } from '../../../../hooks/dyf/use-dyf-eligible-model/use-dyf-eligible-model';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { FMCard, FMCarousel } from '@own/fds-react';

import { FMCardProps } from '@own/fds-react/dist/types/fm-card.types';
import { useGVTCard, useHTVCard, useLivestreamCard } from './trio-cards';
import { GVT5050Card } from './5050-cards/dyf-gvt-5050/dyf-gvt-5050';
import { DyfHtv5050 } from './5050-cards/dyf-htv-5050/dyf-htv-5050';

enum CardRenderType {
    CardTrio,
    DuoCards,
    Gvt5050,
    HTV5050,
    NoCards,
}

interface Props {
    eligibleModelData: EligibleModelData;
    discoverYourFordText: string;
    isMobile?: boolean;
}

export const FeatureGuideCards = (props: Props) => {
    const { eligibleModelData, discoverYourFordText } = props;
    const serverContext = useContext(ServerContext);
    const baseRoot = serverContext.root ? serverContext.root.slice(0, -1) : '';

    const [fireEvents] = useAnalytics();
    const onClickAnalytics = (card: string, button: string) => {
        fireEvents('dyf-cards-onclick', undefined, {
            dyfComponentName: card,
            categoryName: button,
        });
    };

    const gvtCard = useGVTCard({
        href: `${baseRoot}/discover-your-${serverContext.brand.toLowerCase()}/scheduling`,
        onClickAnalytics,
        modelDisplayName: eligibleModelData.modelDisplayName,
    });

    const htvCard = useHTVCard({
        href: `${baseRoot}/vehicle/${eligibleModelData.modelAlias}/${eligibleModelData.modelYear}/how-to-videos/video-library`,
        onClickAnalytics,
        modelDisplayName: eligibleModelData.modelDisplayName,
    });

    const livestreamCard = useLivestreamCard({
        modelDisplayName: eligibleModelData.modelDisplayName,
        onClickAnalytics,
        href: `${baseRoot}/vehicle/${eligibleModelData.modelAlias}/${
            eligibleModelData.modelYear
        }/discover-your-${serverContext.brand.toLowerCase()}/livestream`,
    });

    const cardRenderType: CardRenderType = useMemo(() => {
        if (
            !eligibleModelData.hideHTV &&
            eligibleModelData.eligibleLivestream &&
            eligibleModelData.eligibleGVT
        ) {
            return CardRenderType.CardTrio;
        } else if (
            !eligibleModelData.hideHTV &&
            !eligibleModelData.eligibleLivestream &&
            eligibleModelData.eligibleGVT
        ) {
            return CardRenderType.DuoCards;
        } else if (
            eligibleModelData.hideHTV &&
            !eligibleModelData.eligibleLivestream &&
            eligibleModelData.eligibleGVT
        ) {
            return CardRenderType.Gvt5050;
        } else if (
            !eligibleModelData.hideHTV &&
            !eligibleModelData.eligibleGVT &&
            !eligibleModelData.eligibleLivestream
        ) {
            return CardRenderType.HTV5050;
        } else {
            return CardRenderType.NoCards;
        }
    }, [eligibleModelData]);

    switch (cardRenderType) {
        case CardRenderType.CardTrio:
            return (
                <div
                    className="onboarding-cards-section dyf-feature-guide-cards-trio"
                    data-testid="onboarding-feature-guide-carousel-content"
                >
                    <FMCarousel
                        carouselTheme="light"
                        isUniMode
                        render={(card: FMCardProps) => (
                            <FMCard
                                {...card}
                                ctaType="primary"
                                ctaTheme="light"
                                ctaOnClick={() =>
                                    onClickAnalytics(
                                        card.title ?? '',
                                        card.ctaLabel
                                    )
                                }
                            >
                                {card.children}
                            </FMCard>
                        )}
                        items={[gvtCard, htvCard, livestreamCard]}
                    />
                </div>
            );

        case CardRenderType.DuoCards:
            return (
                <div
                    className="onboarding-cards-section dyf-feature-guide-cards-trio"
                    data-testid="onboarding-feature-guide-carousel-content"
                >
                    <FMCarousel
                        carouselTheme="light"
                        isUniMode
                        render={(card: FMCardProps) => (
                            <FMCard
                                {...card}
                                ctaType={'primary'}
                                ctaTheme={'light'}
                                ctaOnClick={() =>
                                    onClickAnalytics(
                                        card.title ?? '',
                                        card.ctaLabel
                                    )
                                }
                            >
                                {card?.children}
                            </FMCard>
                        )}
                        items={[gvtCard, htvCard]}
                    />
                </div>
            );

        case CardRenderType.Gvt5050:
            return (
                <div
                    className="dyf-gvt-card-5050"
                    data-testid="dyf-gvt-5050-card"
                >
                    <GVT5050Card
                        discoverYourFordText={discoverYourFordText}
                        baseRoot={baseRoot}
                        brand={serverContext.brand}
                        mediaSide="left"
                        onClick={({ categoryName, dyfComponentName }) =>
                            onClickAnalytics(categoryName, dyfComponentName)
                        }
                        isMobile={props.isMobile}
                    />
                </div>
            );

        case CardRenderType.HTV5050:
            return (
                <div
                    className="dyf-htv-card-5050"
                    data-testid="dyf-htv-5050-card"
                >
                    <DyfHtv5050
                        discoverYourFordText={discoverYourFordText}
                        baseRoot={baseRoot}
                        eligibleModelData={eligibleModelData}
                        isMobile={props.isMobile}
                    />
                </div>
            );

        case CardRenderType.NoCards:
        default:
            return null;
    }
};
